import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Home,
  Briefcase,
  MapPin,
  Plus,
  Save,
  Loader2,
  Navigation,
  ArrowLeft,
  Star,
  CheckCircle2,
  Search,
  LocateFixed,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

import Navbar from "../LandingPage/Navbar";
import Footer from "../LandingPage/Footer";
import { GlobalContext } from "../../Context/GlobalContext";
import useCustomerDetails from "../Services/Customer/useCustomerDetails";

const Address = () => {
  const navigate = useNavigate();
  const searchTimerRef = useRef(null);

  const { userCode, latitude, longitude } = useContext(GlobalContext);

  const {
    updateFavouriteAddressApi,
    getFavouriteAddressApi,
    updateFavouriteAddressLoading,
    getFavouriteAddressLoading,
    placesAutoCompletionApi,
    placesAutoCompletionLoading,
  } = useCustomerDetails();

  const [addresses, setAddresses] = useState([]);
  const [selectedFavouriteKey, setSelectedFavouriteKey] = useState("");
  const [savingFavouriteIndex, setSavingFavouriteIndex] = useState(null);

  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [resolvingLatLong, setResolvingLatLong] = useState(false);

  const [formData, setFormData] = useState({
    address: "",
    latitude: "",
    longitude: "",
    addressType: "Home",
    isFavourite: false,
  });

  const extractAddressList = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse?.addresses ||
      [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.response)) return data.response;
    if (Array.isArray(data?.addresses)) return data.addresses;
    if (data && typeof data === "object") return [data];

    return [];
  };

  const extractPlaceSuggestions = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse?.predictions ||
      apiResponse?.suggestions ||
      [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.predictions)) return data.predictions;
    if (Array.isArray(data?.suggestions)) return data.suggestions;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.response)) return data.response;

    return [];
  };

  const normalizeSuggestion = (item) => {
    const address =
      item?.description ||
      item?.formatted_address ||
      item?.address ||
      item?.name ||
      item?.main_text ||
      item?.structured_formatting?.main_text ||
      item?.terms?.map((term) => term?.value).join(", ") ||
      "";

    const secondaryText =
      item?.secondary_text ||
      item?.structured_formatting?.secondary_text ||
      item?.subtitle ||
      "";

    const lat =
      item?.latitude ||
      item?.lat ||
      item?.location?.lat ||
      item?.location?.latitude ||
      item?.geometry?.location?.lat ||
      item?.geometry?.location?.latitude ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.long ||
      item?.location?.lng ||
      item?.location?.longitude ||
      item?.geometry?.location?.lng ||
      item?.geometry?.location?.longitude ||
      "";

    return {
      ...item,
      displayAddress: address,
      secondaryText,
      latitude: lat,
      longitude: lng,
      placeId: item?.place_id || item?.placeId || "",
    };
  };

  const cleanAddressTypeFromAddress = (address) => {
    return String(address || "").replace(/\s*\((Home|Office|Other)\)\s*$/i, "");
  };

  const getAddressType = (item) => {
    const directType =
      item?.addressType ||
      item?.address_type ||
      item?.type ||
      item?.favouriteName ||
      item?.favourite ||
      "";

    if (directType) return directType;

    const address = item?.address || item?.fullAddress || item?.location || "";

    const match = String(address).match(/\((Home|Office|Other)\)\s*$/i);

    if (match?.[1]) return match[1];

    return "Address";
  };

  const getAddressKey = (item) => {
    const address = item?.address || item?.fullAddress || item?.location || "";
    const lat =
      item?.latitude ||
      item?.lat ||
      item?.addressLatitude ||
      item?.address_latitude ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.long ||
      item?.addressLongitude ||
      item?.address_longitude ||
      "";

    return `${address}-${lat}-${lng}`;
  };

  const getLatLongFromAddressText = async (addressText) => {
    if (!addressText) return null;

    const googleKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!googleKey) {
      toast.error("Google Maps API key missing in .env");
      return null;
    }

    try {
      setResolvingLatLong(true);

      const searchQuery = `${addressText}, India`;

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          searchQuery,
        )}&key=${googleKey}`,
      );

      const data = await response.json();

      if (data?.status === "OK" && data?.results?.length > 0) {
        const selectedPlace = data.results[0];

        const lat = selectedPlace?.geometry?.location?.lat;
        const lng = selectedPlace?.geometry?.location?.lng;

        if (!lat || !lng) {
          toast.error("Latitude/longitude not found for this address");
          return null;
        }

        return {
          address: selectedPlace?.formatted_address || addressText,
          latitude: String(lat),
          longitude: String(lng),
        };
      }

      if (data?.status === "ZERO_RESULTS") {
        toast.error(
          "Location not found. Please enter a more complete address.",
        );
      } else if (data?.status === "REQUEST_DENIED") {
        toast.error("Google Maps request denied. Check API key permissions.");
      } else {
        toast.error(data?.error_message || "Unable to get latitude/longitude");
      }

      return null;
    } catch (error) {
      console.error("Geocode fallback error:", error);
      toast.error("Unable to get latitude/longitude");
      return null;
    } finally {
      setResolvingLatLong(false);
    }
  };

  const fetchFavouriteAddresses = async () => {
    if (!userCode) return;

    const result = await getFavouriteAddressApi({
      userId: userCode,
    });

    const list = extractAddressList(result);

    setAddresses(list);

    const savedFavouriteKey = localStorage.getItem(
      `selected_favourite_address_${userCode}`,
    );

    if (savedFavouriteKey) {
      setSelectedFavouriteKey(savedFavouriteKey);
      return;
    }

    if (list.length > 0) {
      const firstAddressKey = getAddressKey(list[0]);

      setSelectedFavouriteKey(firstAddressKey);
      localStorage.setItem(
        `selected_favourite_address_${userCode}`,
        firstAddressKey,
      );
    }
  };

  useEffect(() => {
    fetchFavouriteAddresses();

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [userCode]);

  const handleUseCurrentLocation = () => {
    if (!latitude || !longitude) {
      toast.error("Current location not available");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      latitude: String(latitude),
      longitude: String(longitude),
    }));

    toast.success("Current location added");
  };

  const handleSearchPlaces = async (value) => {
    if (!value || value.trim().length < 3) {
      setPlaceSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const result = await placesAutoCompletionApi({
      input: value,
      latitude: Number(latitude || 0),
      longitude: Number(longitude || 0),
      radius: 50000,
    });

    const suggestions =
      extractPlaceSuggestions(result).map(normalizeSuggestion);

    setPlaceSuggestions(suggestions);
    setShowSuggestions(true);
  };

  const handleAddressInputChange = (e) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      address: value,
      latitude: "",
      longitude: "",
    }));

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      handleSearchPlaces(value);
    }, 450);
  };

  const handleSelectSuggestion = async (suggestion) => {
    const selected = normalizeSuggestion(suggestion);

    const selectedAddress = selected.displayAddress || formData.address;

    setShowSuggestions(false);
    setPlaceSuggestions([]);

    if (selected.latitude && selected.longitude) {
      setFormData((prev) => ({
        ...prev,
        address: selectedAddress,
        latitude: String(selected.latitude),
        longitude: String(selected.longitude),
      }));

      toast.success("Address selected");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      address: selectedAddress,
      latitude: "",
      longitude: "",
    }));

    const resolvedLocation = await getLatLongFromAddressText(selectedAddress);

    if (resolvedLocation) {
      setFormData((prev) => ({
        ...prev,
        address: resolvedLocation.address,
        latitude: resolvedLocation.latitude,
        longitude: resolvedLocation.longitude,
      }));

      toast.success("Address and location selected");
    } else {
      toast.error("Lat/long not found. Please select another address.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userCode) {
      toast.error("User not found. Please login again.");
      return;
    }

    if (!formData.address.trim()) {
      toast.error("Please enter address");
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      toast.error("Please select an address that has latitude and longitude");
      return;
    }

    const finalAddress = cleanAddressTypeFromAddress(formData.address);

    const result = await updateFavouriteAddressApi({
      userId: userCode,
      latitude: String(formData.latitude),
      longitude: String(formData.longitude),
      address: finalAddress,
      favourite: formData.addressType, // Home / Office / Other
    });

    if (result?.responseCode === "00" || result?.status === true || result) {
      toast.success(result?.responseMessage || "Address saved successfully");

      const newAddressItem = {
        address: finalAddress,
        latitude: String(formData.latitude),
        longitude: String(formData.longitude),
        favourite: formData.addressType,
        addressType: formData.addressType,
      };

      const newAddressKey = getAddressKey(newAddressItem);

      /**
       * First address auto favourite
       */
      if (addresses.length === 0) {
        setSelectedFavouriteKey(newAddressKey);
        localStorage.setItem(
          `selected_favourite_address_${userCode}`,
          newAddressKey,
        );
      }

      setFormData({
        address: "",
        latitude: "",
        longitude: "",
        addressType: "Home",
        isFavourite: false,
      });

      setPlaceSuggestions([]);
      setShowSuggestions(false);

      fetchFavouriteAddresses();
    }
  };

  const handleSelectSavedFavourite = (item, index) => {
    const address = item?.address || item?.fullAddress || item?.location || "";
    const lat =
      item?.latitude ||
      item?.lat ||
      item?.addressLatitude ||
      item?.address_latitude ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.long ||
      item?.addressLongitude ||
      item?.address_longitude ||
      "";

    if (!address || !lat || !lng) {
      toast.error("This address does not have complete location data");
      return;
    }

    const addressKey = getAddressKey(item);

    setSelectedFavouriteKey(addressKey);
    localStorage.setItem(`selected_favourite_address_${userCode}`, addressKey);

    toast.success("Favourite address selected");
  };

  const getAddressIcon = (type) => {
    const value = String(type || "").toLowerCase();

    if (value.includes("home")) return Home;
    if (value.includes("work") || value.includes("office")) return Briefcase;

    return MapPin;
  };

  return (
    <>
      <Navbar />
      <ToastContainer />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-100 px-4 pb-12 pt-28">
        <div className="mx-auto max-w-[1120px]">
          <button
            onClick={() => navigate(-1)}
            className="mb-5 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-100"
          >
            <ArrowLeft size={17} />
            Back
          </button>

          <div className="mb-8 overflow-hidden rounded-[32px] bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 p-6 text-white shadow-[0_18px_50px_rgba(37,99,235,0.25)] md:p-8">
            <div className="relative">
              <div className="absolute right-[-70px] top-[-90px] h-52 w-52 rounded-full bg-white/10" />
              <div className="absolute bottom-[-100px] left-[-80px] h-56 w-56 rounded-full bg-white/10" />

              <div className="relative flex flex-col justify-between gap-6 md:flex-row md:items-center">
                <div>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                    <MapPin size={30} />
                  </div>

                  <h1 className="text-2xl font-bold md:text-4xl">
                    Saved Addresses
                  </h1>

                  <p className="mt-2 max-w-[620px] text-sm text-white/75 md:text-base">
                    Search nearby addresses, save them as Home, Office, or
                    Other, and click any saved address to make it favourite.
                  </p>
                </div>

                <div className="rounded-3xl bg-white/15 px-5 py-4 backdrop-blur">
                  <p className="text-sm text-white/70">Total Addresses</p>
                  <h2 className="mt-1 text-3xl font-bold">
                    {addresses?.length || 0}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
            {/* FORM */}
            <div className="rounded-[28px] bg-white p-5 shadow-[0_15px_40px_rgba(15,23,42,0.08)] md:p-6">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                  <Plus size={22} />
                </div>

                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    Add Address
                  </h2>
                  <p className="text-sm text-slate-500">
                    Type address and select from nearby suggestions
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Address Type
                  </label>

                  <div className="grid grid-cols-3 gap-2">
                    {["Home", "Office", "Other"].map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            addressType: item,
                          }))
                        }
                        className={`rounded-2xl border px-3 py-3 text-sm font-semibold transition ${
                          formData.addressType === item
                            ? "border-blue-600 bg-blue-600 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Search Address
                  </label>

                  <div className="relative">
                    <Search
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleAddressInputChange}
                      onFocus={() => {
                        if (placeSuggestions.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      placeholder="Example: Sector 52 Noida"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-11 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />

                    {(placesAutoCompletionLoading || resolvingLatLong) && (
                      <Loader2
                        size={18}
                        className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-blue-600"
                      />
                    )}
                  </div>

                  {showSuggestions && (
                    <div className="absolute left-0 right-0 top-[78px] z-50 max-h-[260px] overflow-y-auto rounded-2xl border border-slate-100 bg-white p-2 shadow-2xl">
                      {placeSuggestions.length > 0 ? (
                        placeSuggestions.map((item, index) => {
                          const suggestion = normalizeSuggestion(item);

                          return (
                            <button
                              type="button"
                              key={
                                item?.place_id ||
                                item?.placeId ||
                                item?.id ||
                                `${suggestion.displayAddress}-${index}`
                              }
                              onClick={() => handleSelectSuggestion(item)}
                              className="flex w-full gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-blue-50"
                            >
                              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                                <LocateFixed size={17} />
                              </span>

                              <span className="min-w-0">
                                <span className="block truncate text-sm font-semibold text-slate-800">
                                  {suggestion.displayAddress ||
                                    "Unnamed location"}
                                </span>

                                {suggestion.secondaryText && (
                                  <span className="mt-0.5 block truncate text-xs text-slate-500">
                                    {suggestion.secondaryText}
                                  </span>
                                )}

                                {suggestion.latitude &&
                                  suggestion.longitude && (
                                    <span className="mt-1 block text-[11px] text-slate-400">
                                      {suggestion.latitude},{" "}
                                      {suggestion.longitude}
                                    </span>
                                  )}
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-4 py-5 text-center text-sm text-slate-500">
                          No nearby address found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Latitude
                    </label>

                    <input
                      type="text"
                      name="latitude"
                      value={formData.latitude}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          latitude: e.target.value,
                        }))
                      }
                      placeholder="Auto filled"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Longitude
                    </label>

                    <input
                      type="text"
                      name="longitude"
                      value={formData.longitude}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          longitude: e.target.value,
                        }))
                      }
                      placeholder="Auto filled"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                >
                  <Navigation size={17} />
                  Use Current Location
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      isFavourite: !prev.isFavourite,
                    }))
                  }
                  className={`flex w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                    formData.isFavourite
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Star
                    size={17}
                    className={formData.isFavourite ? "fill-white" : ""}
                  />
                  {formData.isFavourite
                    ? "Marked as Favourite"
                    : "Mark as Favourite"}
                </button>

                <button
                  type="submit"
                  disabled={updateFavouriteAddressLoading || resolvingLatLong}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {updateFavouriteAddressLoading || resolvingLatLong ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {resolvingLatLong ? "Getting Location..." : "Saving..."}
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Save Address
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* LIST */}
            <div className="rounded-[28px] bg-white p-5 shadow-[0_15px_40px_rgba(15,23,42,0.08)] md:p-6">
              <div className="mb-6 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    Your Addresses
                  </h2>
                  <p className="text-sm text-slate-500">
                    Click any saved address to make it favourite
                  </p>
                </div>

                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                  <MapPin size={21} />
                </div>
              </div>

              {getFavouriteAddressLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="rounded-3xl border border-slate-100 bg-slate-50 p-4"
                    >
                      <div className="mb-3 h-4 w-28 animate-pulse rounded bg-slate-200" />
                      <div className="mb-2 h-3 w-full animate-pulse rounded bg-slate-200" />
                      <div className="h-3 w-2/3 animate-pulse rounded bg-slate-200" />
                    </div>
                  ))}
                </div>
              ) : addresses?.length > 0 ? (
                <div className="max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
                  <div className="grid gap-4">
                    {addresses.map((item, index) => {
                      const favourite =
                        selectedFavouriteKey === getAddressKey(item);

                      const type = getAddressType(item);
                      const Icon = getAddressIcon(type);

                      const rawAddress =
                        item?.address ||
                        item?.fullAddress ||
                        item?.location ||
                        "Address not available";

                      const address = cleanAddressTypeFromAddress(rawAddress);

                      const lat =
                        item?.latitude ||
                        item?.lat ||
                        item?.addressLatitude ||
                        item?.address_latitude ||
                        "";

                      const lng =
                        item?.longitude ||
                        item?.lng ||
                        item?.long ||
                        item?.addressLongitude ||
                        item?.address_longitude ||
                        "";

                      return (
                        <button
                          type="button"
                          key={item?.id || item?.pk_id || index}
                          onClick={() =>
                            handleSelectSavedFavourite(item, index)
                          }
                          className={`group w-full rounded-3xl border p-4 text-left transition ${
                            favourite
                              ? "border-blue-300 bg-blue-50 shadow-sm"
                              : "border-slate-100 bg-slate-50 hover:border-blue-100 hover:bg-blue-50/40"
                          }`}
                        >
                          <div className="flex gap-4">
                            <div
                              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-md ${
                                favourite
                                  ? "bg-blue-600 text-white"
                                  : "bg-white text-blue-700"
                              }`}
                            >
                              {savingFavouriteIndex === index ? (
                                <Loader2 size={22} className="animate-spin" />
                              ) : (
                                <Icon size={22} />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex flex-wrap items-center gap-2">
                                <h3 className="font-bold text-slate-900">
                                  {type}
                                </h3>

                                {favourite && (
                                  <span className="flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                                    <CheckCircle2 size={12} />
                                    Favourite
                                  </span>
                                )}

                                {!favourite && (
                                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                                    Click to favourite
                                  </span>
                                )}
                              </div>

                              <p className="text-sm leading-6 text-slate-600">
                                {address}
                              </p>

                              <div className="mt-3 flex flex-wrap gap-2">
                                {lat ? (
                                  <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500">
                                    Lat: {lat}
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-600">
                                    Lat missing
                                  </span>
                                )}

                                {lng ? (
                                  <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-500">
                                    Lng: {lng}
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-600">
                                    Lng missing
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[350px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-5 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-blue-100 text-blue-700">
                    <MapPin size={32} />
                  </div>

                  <h3 className="text-lg font-bold text-slate-900">
                    No address added yet
                  </h3>

                  <p className="mt-2 max-w-[320px] text-sm text-slate-500">
                    Search your address and save it with exact location.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Address;
